// src/pages/AnalysisPage.tsx
import React from 'react';
import { ArrowLeft, User, Calendar, Download, Share2 } from 'lucide-react';

interface HealthMetric {
  test_name: string;
  value: string;
  unit?: string;
  reference_range?: string;
  status?: 'normal' | 'high' | 'low' | 'critical';
}

interface PatientInfo {
  name?: string;
  age?: string;
  gender?: string;
  report_date?: string;
}

interface AnalysisData {
  filename: string;
  patient_info: PatientInfo;
  health_metrics: HealthMetric[];
  metrics_count: number;
  processing_info?: {
    processing_time?: number;
    extraction_time?: number;
  };
}

interface AnalysisPageProps {
  data: AnalysisData;
  onBack: () => void;
}

export const AnalysisPage: React.FC<AnalysisPageProps> = ({ data, onBack }) => {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'normal': return '#059669';
      case 'high': return '#dc2626';
      case 'low': return '#f59e0b';
      case 'critical': return '#991b1b';
      default: return 'var(--color-muted)';
    }
  };

  const getStatusBgColor = (status?: string) => {
    switch (status) {
      case 'normal': return '#d1fae5';
      case 'high': return '#fee2e2';
      case 'low': return '#fef3c7';
      case 'critical': return '#fecaca';
      default: return 'var(--color-surface)';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: 'var(--font-primary)'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: 'var(--space-4) var(--space-6)',
        fontFamily: 'var(--font-primary)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <button
              onClick={onBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                fontFamily: 'var(--font-primary)',
                color: 'var(--color-primary)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <ArrowLeft size={16} />
              Back to Upload
            </button>
            <h1 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-bold)',
              fontFamily: 'var(--font-primary)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Health Analysis Report
            </h1>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              fontFamily: 'var(--font-primary)',
              color: 'var(--color-primary)'
            }}>
              <Download size={16} />
              Download
            </button>
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              fontFamily: 'var(--font-primary)',
              color: 'var(--color-primary)'
            }}>
              <Share2 size={16} />
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: 'var(--space-6)',
        fontFamily: 'var(--font-primary)'
      }}>
        {/* Document Info */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 'var(--radius)',
          padding: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
          fontFamily: 'var(--font-primary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-3)'
        }}>
          <p style={{
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-primary)',
            color: 'var(--color-muted)',
            margin: 0
          }}>
            Document: <strong style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-primary)' }}>{data.filename}</strong>
          </p>
          {(data.processing_info?.processing_time || data.processing_info?.extraction_time) && (
            <p style={{
              fontSize: 'var(--text-sm)',
              fontFamily: 'var(--font-primary)',
              color: 'var(--color-muted)',
              margin: 0
            }}>
              Processing time: <strong style={{ color: 'var(--color-success)', fontFamily: 'var(--font-primary)' }}>
                {(data.processing_info.processing_time || data.processing_info.extraction_time)?.toFixed(2)}s
              </strong>
            </p>
          )}
        </div>

        {/* Patient Information Card */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 'var(--radius)',
          marginBottom: 'var(--space-6)',
          overflow: 'hidden',
          fontFamily: 'var(--font-primary)'
        }}>
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: '#dbeafe',
            borderBottom: '1px solid #93c5fd'
          }}>
            <h2 style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              fontFamily: 'var(--font-primary)',
              color: '#1e40af',
              margin: 0
            }}>
              <User size={20} />
              Patient Information
            </h2>
          </div>

          <div style={{
            padding: 'var(--space-4)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-4)'
          }}>
            {data.patient_info.name && (
              <div>
                <p style={{
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-1) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Name</p>
                <p style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-primary)',
                  margin: 0
                }}>
                  {data.patient_info.name}
                </p>
              </div>
            )}

            {data.patient_info.age && (
              <div>
                <p style={{
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-1) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Age</p>
                <p style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-primary)',
                  margin: 0
                }}>
                  {data.patient_info.age}
                </p>
              </div>
            )}

            {data.patient_info.gender && (
              <div>
                <p style={{
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-1) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Gender</p>
                <p style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-primary)',
                  margin: 0
                }}>
                  {data.patient_info.gender}
                </p>
              </div>
            )}

            {data.patient_info.report_date && (
              <div>
                <p style={{
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-1) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Report Date</p>
                <p style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-primary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <Calendar size={14} />
                  {data.patient_info.report_date}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Health Metrics Table */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          fontFamily: 'var(--font-primary)'
        }}>
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: '#f0fdf4',
            borderBottom: '1px solid #86efac',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              fontFamily: 'var(--font-primary)',
              color: '#15803d',
              margin: 0
            }}>
              Health Metrics ({data.metrics_count || data.health_metrics.length})
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--font-primary)'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <th style={{
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-semibold)',
                    fontFamily: 'var(--font-primary)',
                    color: 'var(--color-muted)',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Test Name</th>
                  <th style={{
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-semibold)',
                    fontFamily: 'var(--font-primary)',
                    color: 'var(--color-muted)',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Value</th>
                  <th style={{
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-semibold)',
                    fontFamily: 'var(--font-primary)',
                    color: 'var(--color-muted)',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Reference Range</th>
                  <th style={{
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-semibold)',
                    fontFamily: 'var(--font-primary)',
                    color: 'var(--color-muted)',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.health_metrics.map((metric, index) => (
                  <tr key={index} style={{
                    borderBottom: index < data.health_metrics.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}>
                    <td style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      fontFamily: 'var(--font-primary)',
                      color: 'var(--color-primary)'
                    }}>
                      {metric.test_name}
                    </td>
                    <td style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'var(--font-primary)',
                      color: 'var(--color-primary)'
                    }}>
                      {metric.value} {metric.unit && <span style={{ color: 'var(--color-muted)', fontFamily: 'var(--font-primary)' }}>{metric.unit}</span>}
                    </td>
                    <td style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'var(--font-primary)',
                      color: 'var(--color-secondary)'
                    }}>
                      {metric.reference_range || '-'}
                    </td>
                    <td style={{
                      padding: 'var(--space-3) var(--space-4)'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        padding: 'var(--space-1) var(--space-2)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        fontFamily: 'var(--font-primary)',
                        color: getStatusColor(metric.status),
                        backgroundColor: getStatusBgColor(metric.status),
                        borderRadius: 'var(--radius)',
                        textTransform: 'capitalize'
                      }}>
                        {metric.status || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};